"use client";

import { useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

/**
 * Interactive figures for "How a conversation becomes a memory" (agent-memory
 * part 2 — Capture). These two concepts are variable RELATIONSHIPS you only grasp
 * by moving an input, so they're the only figures in the post that are playable:
 *   - salience = max(intensity-vs-your-baseline, consequence[act])
 *   - stability S = 3 + 27·salience  →  retrievability R = (1 + (19/81)·Δt/S)^-0.5
 * Constants mirror the memory library (modules/amygdala.ts, types.ts, modules/recall.ts).
 * SVG colours come from the site's CSS-var tokens, so both themes are automatic.
 */

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/* colour tokens — resolve per-theme at paint time */
const C = {
  signal: "var(--color-signal)",
  signalDeep: "var(--color-signal-deep)",
  sage: "var(--color-sage)",
  grid: "var(--color-border-subtle)",
  axis: "var(--color-border)",
  text: "var(--color-foreground-muted)",
  faint: "var(--color-foreground-faint)",
};

function Frame({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <figure className="overflow-hidden rounded-xl border border-border-subtle bg-surface-elevated">
      <div className="p-5 sm:p-6">{children}</div>
      <figcaption className="border-t border-border-subtle px-5 py-3 font-mono text-xs leading-relaxed tracking-[0.03em] text-foreground-muted">
        {caption}
      </figcaption>
    </figure>
  );
}

function Seg<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: readonly { key: T; label: string }[];
  value: T;
  onChange: Dispatch<SetStateAction<T>>;
  ariaLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex flex-wrap overflow-hidden rounded-lg border border-border"
    >
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          aria-pressed={o.key === value}
          onClick={() => onChange(o.key)}
          className={`border-r border-border-subtle px-3 py-2 font-mono text-xs last:border-r-0 ${
            o.key === value
              ? "bg-ink text-cream"
              : "text-foreground-muted hover:bg-cream-warm"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ================= salience calculator ================= */

const CONSEQUENCE: Record<string, number> = {
  correction: 1.0,
  decision: 0.8,
  commitment: 0.8,
  retraction: 0.6,
  observation: 0.2,
  routine: 0.0,
};

const PEOPLE = [
  { key: "terse", label: "Terse & calm", norm: 0.12, blurb: "lowercase fragments, no exclamation marks" },
  { key: "neutral", label: "Even-keeled", norm: 0.35, blurb: "ordinary punctuation, occasional emphasis" },
  { key: "effusive", label: "Effusive & loud", norm: 0.62, blurb: "caps, emoji and exclamation marks are normal" },
] as const;

const MESSAGES = [
  { key: "plain", label: "a meeting time", text: "the meeting is on Wednesday" },
  { key: "fix", label: "a fix", text: "no, it's Thursday, not Wednesday" },
  { key: "loud", label: "an angry fix", text: "NO WE NEVER SAID FRIDAY, THIS IS WRONG!!" },
] as const;

/* Transparent stand-in for the LLM intensity judge: emphasis features → 0..1. */
const emphasis = (text: string): number => {
  const letters = (text.match(/[a-z]/gi) ?? []).length;
  const caps = (text.match(/[A-Z]/g) ?? []).length;
  const capsRatio = letters ? caps / (caps + letters) : 0;
  const bangs = Math.min(1, (text.match(/!/g) ?? []).length / 2);
  const intensifiers = /\b(never|always|wrong|urgent|asap|critical|now|huge|hate|love|must|finally)\b/gi;
  const inten = Math.min(1, (text.match(intensifiers) ?? []).length / 2);
  return clamp(0.15 + 0.5 * capsRatio + 0.22 * bangs + 0.3 * inten, 0, 1);
};

function Bar({ label, sub, value, strong }: { label: string; sub: string; value: number; strong?: boolean }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.05em] text-foreground-muted">
          {label} <span className="text-foreground-faint normal-case tracking-normal">{sub}</span>
        </span>
      </div>
      <div className="h-5 overflow-hidden rounded-md border border-border-subtle bg-surface-sunken">
        <div
          className={`h-full rounded-md transition-[width] duration-500 ${strong ? "bg-signal" : "bg-signal/45"}`}
          style={{ width: `${value * 100}%` }}
        />
      </div>
    </div>
  );
}

/* qualitative band for a 0..1 salience — the post shows the shape, not the dial value. */
const salienceWord = (s: number): string =>
  s >= 0.8 ? "peak" : s >= 0.55 ? "high" : s >= 0.3 ? "moderate" : "low";

/* The act sets the floor. The reader picks it directly, which is also how you
 * can watch a correction lose its weight by being tagged as something smaller. */
const TAGS = [
  { key: "correction", label: "correction" },
  { key: "decision", label: "decision" },
  { key: "observation", label: "observation" },
  { key: "routine", label: "routine" },
] as const;

export function FigSalienceLive() {
  const [msgKey, setMsgKey] = useState<(typeof MESSAGES)[number]["key"]>("loud");
  const [tagKey, setTagKey] = useState<(typeof TAGS)[number]["key"]>("observation");
  const [personKey, setPersonKey] = useState<(typeof PEOPLE)[number]["key"]>("terse");

  const msg = MESSAGES.find((m) => m.key === msgKey) ?? MESSAGES[0];
  const person = PEOPLE.find((p) => p.key === personKey) ?? PEOPLE[1];
  const floor = CONSEQUENCE[tagKey];
  const intensity = clamp((emphasis(msg.text) - person.norm) / (1 - person.norm), 0, 1);
  const salience = Math.max(floor, intensity);
  const intensityWins = intensity > floor;

  return (
    <Frame caption="Change any of the three and watch the salience move. (Intensity is used only when it's higher than the floor.)">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            what did they write?
          </div>
          <Seg
            options={MESSAGES.map((m) => ({ key: m.key, label: m.label }))}
            value={msgKey}
            onChange={setMsgKey}
            ariaLabel="the message"
          />
          <p className="mt-2 rounded-md border border-border-subtle bg-cream-warm px-3 py-2 text-sm text-foreground-strong">
            {msg.text}
          </p>
        </div>
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            what kind of act is it?
          </div>
          <Seg
            options={TAGS.map((t) => ({ key: t.key, label: t.label }))}
            value={tagKey}
            onChange={setTagKey}
            ariaLabel="the act"
          />
        </div>
        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            whose message is it?
          </div>
          <Seg
            options={PEOPLE.map((p) => ({ key: p.key, label: p.label }))}
            value={personKey}
            onChange={setPersonKey}
            ariaLabel="this person's usual tone"
          />
          <p className="mt-2 text-xs leading-snug text-foreground-muted">{person.blurb}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-[1fr_11rem] sm:items-center">
        <div className="flex flex-col gap-4">
          <Bar label="Floor" sub="from the act" value={floor} strong={!intensityWins} />
          <Bar label="Intensity" sub="vs this person's normal" value={intensity} strong={intensityWins} />
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-foreground-faint">
            the higher of the two wins
          </div>
        </div>
        <div className="rounded-xl border border-border-subtle bg-surface-sunken p-4 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            final salience
          </div>
          <div className="my-1 font-serif text-3xl leading-none text-foreground-strong">
            {salienceWord(salience)}
          </div>
          <span className="mt-2 inline-block rounded-full bg-signal px-2 py-0.5 font-mono text-[10px] text-cream">
            {intensityWins ? "intensity wins" : "floor wins"}
          </span>
        </div>
      </div>
    </Frame>
  );
}

/* ================= forgetting curve ================= */

const seedStability = (s: number): number => 3 + 27 * s;
const retrievability = (dt: number, S: number): number => Math.pow(1 + (19 / 81) * (dt / S), -0.5);

const VB = { w: 620, h: 300, l: 42, r: 14, t: 14, b: 30 };
const MAX_DAYS = 180;
const px = (d: number): number => VB.l + (d / MAX_DAYS) * (VB.w - VB.l - VB.r);
const py = (rv: number): number => VB.t + (1 - rv) * (VB.h - VB.t - VB.b);

const curvePath = (S: number): string => {
  const pts = Array.from({ length: 61 }, (_, i) => {
    const d = (i / 60) * MAX_DAYS;
    return `${px(d).toFixed(1)} ${py(retrievability(d, S)).toFixed(1)}`;
  });
  return `M ${pts.join(" L ")}`;
};

const rNote = (rv: number): string =>
  rv > 0.8 ? "still vivid" : rv > 0.5 ? "retrievable" : rv > 0.3 ? "fading" : "nearly gone";

export function FigForgettingLive() {
  const [salience, setSalience] = useState(0.2);
  const [days, setDays] = useState(45);
  const svgRef = useRef<SVGSVGElement>(null);

  const S = seedStability(salience);
  const R = retrievability(days, S);

  const setDaysFromClientX = (clientX: number): void => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dataX = ((clientX - rect.left) / rect.width) * VB.w;
    const d = ((dataX - VB.l) / (VB.w - VB.l - VB.r)) * MAX_DAYS;
    setDays(clamp(d, 0, MAX_DAYS));
  };

  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <Frame caption="Higher salience makes a memory last longer. It fades on the same curve human forgetting follows: most of it holds through the memory's lifespan, then a long tail that never quite hits zero. Drag the sliders to reshape it and move through time. Reading a memory never changes it, and time only lowers how easily it comes back, never what's stored.">
      <div className="grid gap-5 sm:grid-cols-[1fr_11rem] sm:items-center">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VB.w} ${VB.h}`}
          className="w-full select-none sm:touch-none"
          role="img"
          aria-label="Forgetting curve: retrievability falls over time, and higher salience makes it fall more slowly."
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            setDaysFromClientX(e.clientX);
          }}
          onPointerMove={(e) => {
            if (e.buttons > 0) setDaysFromClientX(e.clientX);
          }}
        >
          {yTicks.map((rv) => (
            <g key={rv}>
              <line x1={VB.l} y1={py(rv)} x2={VB.w - VB.r} y2={py(rv)} stroke={C.grid} strokeWidth={1} />
              <text x={VB.l - 6} y={py(rv)} textAnchor="end" dominantBaseline="middle" fontSize={10} fontFamily="var(--font-mono)" fill={C.faint}>
                {`${Math.round(rv * 100).toString()}%`}
              </text>
            </g>
          ))}
          <text x={(VB.l + VB.w - VB.r) / 2} y={VB.h - 6} textAnchor="middle" fontSize={10} fontFamily="var(--font-mono)" fill={C.faint}>
            time since the memory formed →
          </text>

          {/* ghost reference curves: routine (S=3) and peak (S=30) */}
          <path d={curvePath(seedStability(0))} fill="none" stroke={C.grid} strokeWidth={1} strokeDasharray="3 4" />
          <path d={curvePath(seedStability(1))} fill="none" stroke={C.grid} strokeWidth={1} strokeDasharray="3 4" />
          <text x={VB.w - VB.r - 2} y={py(retrievability(MAX_DAYS, seedStability(1))) - 6} textAnchor="end" fontSize={9} fontFamily="var(--font-mono)" fill={C.faint}>
            a peak moment
          </text>
          <text x={VB.w - VB.r - 2} y={py(retrievability(MAX_DAYS, seedStability(0))) + 12} textAnchor="end" fontSize={9} fontFamily="var(--font-mono)" fill={C.faint}>
            a routine lookup
          </text>

          {/* active curve + area */}
          <path d={`${curvePath(S)} L ${px(MAX_DAYS)} ${py(0)} L ${VB.l} ${py(0)} Z`} fill={C.signal} fillOpacity={0.1} />
          <path d={curvePath(S)} fill="none" stroke={C.signal} strokeWidth={2.4} />

          {/* R=0.9 at Δt = S */}
          <line x1={px(S)} y1={py(0)} x2={px(S)} y2={py(0.9)} stroke={C.sage} strokeWidth={1} strokeDasharray="2 3" />
          <circle cx={px(S)} cy={py(0.9)} r={3.5} fill={C.sage} />
          <text x={px(S) + 5} y={py(0.9) - 7} textAnchor="start" fontSize={9} fontFamily="var(--font-mono)" fill={C.sage}>
            one lifespan
          </text>

          {/* today cursor */}
          <line x1={px(days)} y1={VB.t} x2={px(days)} y2={VB.h - VB.b} stroke={C.signal} strokeWidth={1.5} />
          <circle cx={px(days)} cy={py(R)} r={5} fill={C.signal} />
          <circle cx={px(days)} cy={py(R)} r={2} fill="var(--color-surface-elevated)" />
        </svg>

        <div className="flex flex-col gap-3">
          <div className="rounded-lg border border-border-subtle bg-surface-sunken p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.09em] text-foreground-faint">memory lifespan</div>
            <div className="mt-2 h-3 overflow-hidden rounded-md border border-border-subtle bg-surface">
              <div
                className="h-full rounded-md bg-signal transition-[width] duration-300"
                style={{ width: `${clamp(15 + 85 * salience, 0, 100).toString()}%` }}
              />
            </div>
            <div className="mt-1 font-mono text-[10px] text-signal-deep">
              {salience >= 0.66 ? "long-lived" : salience >= 0.33 ? "medium" : "short-lived"}
            </div>
          </div>
          <div className="rounded-lg border border-border-subtle bg-surface-sunken p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.09em] text-foreground-faint">
              how much is left
            </div>
            <div className="mt-0.5 font-serif text-2xl leading-none tabular-nums text-foreground-strong">
              {Math.round(R * 100)}%
            </div>
            <div className="font-mono text-[10px] text-signal-deep">{rNote(R)}</div>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            salience <span className="text-signal-deep">{salienceWord(salience)}</span>
          </span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={salience}
            onChange={(e) => setSalience(Number(e.target.value))}
            className="mt-2 w-full accent-signal"
            aria-label="salience"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-foreground-faint">
            time elapsed
          </span>
          <input
            type="range"
            min={0}
            max={MAX_DAYS}
            step={1}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="mt-2 w-full accent-signal"
            aria-label="days elapsed"
          />
        </label>
      </div>
    </Frame>
  );
}
